<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue'

import logoCollapsed from '@/app/assets/logo_color_collapsed.svg'
import logo from '@/app/assets/logo_color.svg'
import logoStage from '@/app/assets/logo_color_stage.svg'
import { getConfig } from '@/app/config'
import { useI18n } from '@/app/i18n'
import { groupNav, type NavSection } from '@/app/nav/collect'
import { useAuthStore } from '@/app/stores/auth'
import { useUiStore } from '@/app/stores/ui'
import { useCurrentSoftware } from '@/shared/composables/useCurrentSoftware'
import AppCollapseGroup from '@/shared/ui/AppCollapseGroup.vue'
import AppIcon from '@/shared/ui/AppIcon.vue'

import NavAccountCard from './NavAccountCard.vue'
import NavMenuItem from './NavMenuItem.vue'
import SoftwareSwitcher from './SoftwareSwitcher.vue'
import { useShellMenu } from './useShellMenu'

/**
 * The vertical nav (wp/shell §4.18, redesigned 2026-10 as a floating rounded panel): the software switcher card (the
 * logo in its place until the company wizard is done) with the collapse handle notched into the panel's right edge, then the menu of the current software filtered for the user (src/app/nav) in three labelled
 * blocks — the software's own entries, the workspace entries shared by every employer software (calendar, tasks,
 * documents, messages, trash), and any `others` block a module declares — then the build version and the account card
 * (organization profile, settings, profile, role switch, help, logout). Legacy's Quick add button was dropped (2026-10). Collapsed (80 px, pinned closed and not
 * hovered) it shows icons only, the blocks separated by thin rules.
 *
 * Without a workspace or with the company profile wizard unfinished it shows the header and the account card only —
 * except a Freelancer without any workspace, who gets the menu ("My Resume", OPEN-14 default (a); legacy showed
 * nothing, NEW-6).
 */
const props = defineProps<{ xl: boolean; open: boolean }>()
const emit = defineEmits<{ close: [] }>()

const { t } = useI18n()
const auth = useAuthStore()
const ui = useUiStore()
const config = getConfig()
const { software } = useCurrentSoftware()
/** The menu is scrolled away from its top: a shadow under the header says there are items above. */
const scrolled = ref(false)

/** Pins / unpins; the toggle drops focus first so the shrinking panel is not scrolled to keep it in view. */
function togglePinned(event: Event): void {
  ;(event.currentTarget as HTMLElement | null)?.blur()
  ui.toggleCollapsed()
}

function onMenuScroll(event: Event): void {
  scrolled.value = (event.target as HTMLElement).scrollTop > 2
}

const mini = computed(() => props.xl && ui.navCollapsed && !hovered.value)
/**
 * Hover expansion of a collapsed panel. The handle is a sibling of the panel that overlaps its edge, so moving the
 * pointer from the panel onto the handle leaves the panel: the panel's leave is delayed a moment so the handle's enter
 * can take over, and the handle only holds the panel open when the panel is already expanded — hovering the handle on
 * the collapsed rail must not expand the panel, which would slide the handle out from under the pointer.
 */
const hoveredNav = ref(false)
const hoveredHandle = ref(false)
const hovered = computed(() => hoveredNav.value || hoveredHandle.value)
let leaveTimer: ReturnType<typeof setTimeout> | undefined

function enterNav(): void {
  clearTimeout(leaveTimer)
  hoveredNav.value = true
}
function leaveNav(): void {
  clearTimeout(leaveTimer)
  leaveTimer = setTimeout(() => {
    hoveredNav.value = false
  }, 150)
}
function enterHandle(): void {
  // The handle takes the hover over from the panel: the panel's own flag goes down, else it would stay up for good.
  const expanded = !mini.value
  clearTimeout(leaveTimer)
  hoveredNav.value = false
  hoveredHandle.value = expanded
}
function leaveHandle(): void {
  hoveredHandle.value = false
}
onBeforeUnmount(() => clearTimeout(leaveTimer))
const workspace = computed(() => auth.currentWorkspace)
const wizardDone = computed(() => Boolean(workspace.value && workspace.value.WizzardCompleted))
const freelancerWithoutWorkspace = computed(() => auth.userType === 'Freelancer' && !workspace.value)
const showMenu = computed(() => wizardDone.value || freelancerWithoutWorkspace.value)
/** The menu filtered for the user: the same list the navbar's palette and alerts tray read. */
const menu = useShellMenu()
/** Organization profile and settings: listed by the account card, not the menu blocks. */
const accountItems = computed(() => menu.value.filter((item) => item.section === 'account'))
/** The menu by block (`menu`, `workspace`, `others`), blocks without items left out. */
const sections = computed(() => groupNav(menu.value))
const sectionLabels = computed<Partial<Record<NavSection, string>>>(() => ({
  menu: t('shell.nav.section.menu'),
  workspace: t('shell.nav.section.workspace'),
  others: t('shell.nav.section.others'),
}))
const brand = computed(() => (config.betaFeature ? logoStage : logo))
</script>

<template>
  <aside
    class="vertical-nav"
    :class="{ 'is-mini': mini, 'is-open': open, 'is-xl': xl }"
    data-testid="vertical-nav"
    @mouseenter="enterNav"
    @mouseleave="leaveNav"
  >
    <div class="vertical-nav__header">
      <SoftwareSwitcher v-if="wizardDone" :software="software" :mini="mini" class="vertical-nav__switcher" />
      <RouterLink v-else to="/" class="vertical-nav__brand">
        <img v-if="mini" :src="logoCollapsed" alt="Skileo" class="vertical-nav__logo--mini" />
        <img v-else :src="brand" alt="Skileo" class="vertical-nav__logo" />
      </RouterLink>
      <button
        v-if="!xl"
        type="button"
        class="vertical-nav__toggle"
        :aria-label="t('common.action.close')"
        @click="emit('close')"
      >
        <AppIcon name="XIcon" :size="20" />
      </button>
    </div>

    <div v-if="showMenu" class="vertical-nav__scroll-area">
      <div
        class="vertical-nav__shadow"
        :class="{ 'is-visible': scrolled }"
        aria-hidden="true"
        data-testid="nav-shadow"
      />
      <div class="vertical-nav__content thin-scroll" @scroll.passive="onMenuScroll">
        <AppCollapseGroup>
          <ul class="vertical-nav__menu" data-testid="nav-menu">
            <template v-for="group in sections" :key="group.section">
              <li class="nav-section" role="presentation" :data-section="group.section">
                <span class="nav-section__label">{{ sectionLabels[group.section] }}</span>
                <span class="nav-section__rule" aria-hidden="true" />
              </li>
              <NavMenuItem
                v-for="item in group.items"
                :key="item.id"
                :item="item"
                :software="software ?? 're'"
                :mini="mini"
              />
            </template>
          </ul>
        </AppCollapseGroup>
      </div>
    </div>

    <!-- Always: on desktop the navbar has no user menu, so the card is the one way to the profile and the logout. -->
    <div class="vertical-nav__footer">
      <div v-if="config.buildVersion" class="vertical-nav__version" data-testid="build-version">
        {{ t('shell.nav.hint.version') }} {{ config.buildVersion }}
      </div>
      <NavAccountCard :items="showMenu ? accountItems : []" :software="software ?? 're'" :mini="mini" />
    </div>
  </aside>
  <button
    v-if="xl"
    type="button"
    class="vertical-nav__handle"
    :class="{ 'is-mini': mini }"
    :aria-label="t('shell.nav.action.pin')"
    :aria-pressed="!ui.navCollapsed"
    data-testid="nav-pin"
    @mouseenter="enterHandle"
    @mouseleave="leaveHandle"
    @click="togglePinned"
  >
    <AppIcon :name="ui.navCollapsed ? 'PanelLeftOpenIcon' : 'PanelLeftCloseIcon'" :size="16" />
  </button>
</template>

<style scoped>
.vertical-nav {
  --nav-gutter: 1.3rem;
  position: fixed;
  top: var(--nav-gutter);
  left: var(--nav-gutter);
  z-index: 1031;
  display: flex;
  flex-direction: column;
  width: 260px;
  height: calc(100% - 2 * var(--nav-gutter));
  /* `clip`, not `hidden`: a hidden box still scrolls to keep a focused control in view while the panel shrinks to
     80 px, which left the header and footer 41 px off to the left. */
  overflow: hidden;
  overflow: clip;
  /* Frosted glass: a translucent panel with a backdrop blur (the solid surface stays the fallback); the glow it
     diffuses is its own `::before`, clipped by the rounded edges, so nothing spills onto the page. */
  background: var(--p-surface-0);
  background: color-mix(in srgb, var(--p-surface-0) 80%, transparent);
  backdrop-filter: blur(18px) saturate(160%);
  -webkit-backdrop-filter: blur(18px) saturate(160%);
  border: 1px solid color-mix(in srgb, var(--p-surface-0) 60%, transparent);
  border-radius: var(--p-border-radius-xl);
  box-shadow: 0 8px 30px 0 rgb(34 41 47 / 10%);
  transition:
    width 0.3s ease,
    transform 0.3s ease;
  transform: translateX(-120%);
}
.vertical-nav::before {
  position: absolute;
  inset: -3rem;
  z-index: 0;
  pointer-events: none;
  content: '';
  background:
    radial-gradient(22rem 18rem at 10% 4%, color-mix(in srgb, var(--p-primary-500) 8%, transparent), transparent 70%),
    radial-gradient(16rem 16rem at 110% 52%, color-mix(in srgb, #26a7ff 5%, transparent), transparent 70%),
    radial-gradient(18rem 16rem at 8% 102%, color-mix(in srgb, #ff9f43 6%, transparent), transparent 70%);
  filter: blur(24px);
}
.vertical-nav > * {
  position: relative;
  z-index: 1;
}
.vertical-nav.is-xl,
.vertical-nav.is-open {
  transform: none;
}
.vertical-nav.is-mini {
  width: 80px;
}
/* The header row: the software switcher card (the brand logo only until the wizard is done); below 1200 px the close
   button sits beside it. The collapse toggle is the handle on the panel's edge (`.vertical-nav__handle`). */
.vertical-nav__header {
  display: flex;
  flex: 0 0 auto;
  gap: 0.35rem;
  align-items: center;
  padding: 1rem 1rem 0.5rem;
}
.vertical-nav.is-mini .vertical-nav__header {
  justify-content: center;
  padding: 1rem 0 0.5rem;
}
.vertical-nav__header :deep(.software-switcher) {
  flex: 1 1 auto;
  min-width: 0;
  padding: 0;
}
.vertical-nav__brand {
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  padding-left: 0.6rem;
}
.vertical-nav.is-mini .vertical-nav__brand {
  justify-content: center;
  padding-left: 0;
}
.vertical-nav__logo {
  height: 30px;
}
.vertical-nav__logo--mini {
  width: 27px;
}
.vertical-nav__toggle {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  color: var(--p-surface-600);
  cursor: pointer;
  background: transparent;
  border: 0;
  border-radius: var(--p-border-radius-md);
  transition:
    background 0.15s ease,
    color 0.15s ease;
}
.vertical-nav__toggle:hover {
  color: var(--p-surface-900);
  background: var(--p-surface-100);
}
/* The collapse handle (mockup, owner's pick): a notch on the panel's right edge, 80px below the panel's top — its
   left half flush with the panel (no border), its right half a bordered half circle sticking out. It follows the
   panel's width, hover-expanded included; hovering it counts as hovering the panel, so the panel does not shrink away
   from under the pointer. */
.vertical-nav__handle {
  position: fixed;
  top: calc(var(--layout-gutter, 1.3rem) + 80px);
  left: calc(var(--layout-gutter, 1.3rem) + 260px - 16px);
  z-index: 1032;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  color: var(--p-surface-600);
  cursor: pointer;
  background: var(--p-surface-0);
  border: 0;
  border-radius: 16px;
  transition:
    left 0.3s ease,
    color 0.15s ease,
    background 0.15s ease;
}
.vertical-nav__handle::before {
  position: absolute;
  top: 0;
  left: 50%;
  width: 50%;
  height: 100%;
  box-sizing: border-box;
  pointer-events: none;
  content: '';
  border: 1px solid var(--p-content-border-color);
  border-left: 0;
  border-radius: 0 16px 16px 0;
}
.vertical-nav__handle.is-mini {
  left: calc(var(--layout-gutter, 1.3rem) + 80px - 16px);
}
.vertical-nav__handle:hover {
  color: var(--p-surface-900);
  background: var(--p-surface-50);
}
.vertical-nav__handle:focus-visible {
  outline: 2px solid var(--p-primary-300);
  outline-offset: 2px;
}
.vertical-nav__scroll-area {
  position: relative;
  flex: 1;
  min-height: 0;
}
/* Shown once the menu is scrolled: entries slide under the header instead of vanishing at an exact edge. */
.vertical-nav__shadow {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  z-index: 2;
  height: 14px;
  background: linear-gradient(to bottom, rgb(34 41 47 / 12%), rgb(34 41 47 / 0%));
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
}
.vertical-nav__shadow.is-visible {
  opacity: 1;
}
.vertical-nav__content {
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  padding-bottom: 3rem;
}
.vertical-nav__menu {
  margin: 0;
  padding: 0 12px;
  list-style: none;
}
.vertical-nav__footer {
  flex: 0 0 auto;
}
.vertical-nav__version {
  padding: 0.4rem 0 0;
  font-size: 0.786rem;
  text-align: center;
  color: var(--p-text-muted-color);
}
.vertical-nav.is-mini .vertical-nav__version {
  visibility: hidden;
}

/* Section labels; collapsed, a short rule stands in for them. */
.nav-section {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.4rem 14px 0.4rem;
}
.nav-section__label {
  flex: 1;
  font-size: 0.786rem;
  font-weight: 600;
  color: var(--p-surface-500);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  white-space: nowrap;
}
.nav-section__rule {
  display: none;
  width: 24px;
  height: 1px;
  background: var(--p-content-border-color);
}
.vertical-nav.is-mini .nav-section {
  padding: 1rem 0 0.6rem;
}
.vertical-nav.is-mini .nav-section__label {
  display: none;
}
.vertical-nav.is-mini .nav-section__rule {
  display: block;
}

.vertical-nav :deep(.nav-link) {
  display: flex;
  gap: 0.85rem;
  align-items: center;
  width: 100%;
  margin: 0 0 2px;
  padding: 0 14px;
  height: 40px;
  font: inherit;
  font-weight: 500;
  color: var(--p-text-color);
  white-space: nowrap;
  text-decoration: none;
  cursor: pointer;
  background: transparent;
  border: 0;
  border-radius: var(--p-border-radius-lg);
  transition:
    background 0.15s ease,
    color 0.15s ease;
}
.vertical-nav :deep(.nav-link:hover) {
  background: var(--p-surface-100);
}
.vertical-nav :deep(.nav-link:focus-visible) {
  outline: 2px solid var(--p-primary-300);
  outline-offset: -2px;
}
.vertical-nav :deep(.nav-link__icon) {
  flex: 0 0 auto;
}
.vertical-nav :deep(.nav-link__title) {
  flex: 1;
  overflow: hidden;
  text-align: left;
  text-overflow: ellipsis;
}
.vertical-nav.is-mini :deep(.nav-link) {
  justify-content: center;
  width: 44px;
  margin: 0 auto 4px;
  padding: 0;
  height: 44px;
}
.vertical-nav.is-mini :deep(.nav-link__title),
.vertical-nav.is-mini :deep(.nav-link__arrow),
.vertical-nav.is-mini :deep(.nav-link__badge) {
  display: none;
}
.vertical-nav :deep(.nav-link__arrow) {
  color: var(--p-surface-500);
  transition: transform 0.2s ease;
}
.vertical-nav :deep(.nav-group.is-open > .app-collapse > .nav-link .nav-link__arrow) {
  transform: rotate(90deg);
}
.vertical-nav :deep(.nav-group__items) {
  margin: 0;
  padding: 0;
  list-style: none;
}
.vertical-nav :deep(.nav-group__items .nav-link) {
  height: 36px;
  padding-left: 44px;
  font-size: 0.929rem;
}
.vertical-nav :deep(.nav-group__items .nav-link__icon) {
  display: none;
}
.vertical-nav :deep(.nav-item.is-active > .nav-link),
.vertical-nav :deep(.nav-group.is-active > .app-collapse > .nav-link) {
  font-weight: 600;
  color: var(--p-primary-500);
}
.vertical-nav :deep(.nav-item.is-active > .nav-link) {
  background: color-mix(in srgb, var(--p-primary-500) 10%, var(--p-surface-0));
}
.vertical-nav.is-mini :deep(.nav-group.is-active > .app-collapse > .nav-link) {
  background: color-mix(in srgb, var(--p-primary-500) 10%, var(--p-surface-0));
}
</style>
